import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Input } from '../../components/common'
import { useDocumentTitle } from '../../hooks'
import { useAuthStore } from '../../store/auth.store'
import './Auth.css'

interface LoginFormProps {
  onSuccess?: () => void
  onForgotPassword?: () => void
}

/**
 * LoginForm Component
 * Handles user login with email and password
 * Implements Requirements 1.3, 15.4
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onForgotPassword,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, error, isLoading, clearError } = useAuthStore()

  // Set document title
  useDocumentTitle('pageTitles.login')

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<
    string | null
  >(null)

  // Check if user was redirected due to session expiration
  // Requirement 15.4: Display message when session expires
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirect_after_login')
    if (redirectPath) {
      setSessionExpiredMessage(t('auth.sessionExpired'))
    }
  }, [t])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Email validation
    if (!formData.email) {
      newErrors.email = t('validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid')
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('validation.passwordRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    // Clear global error
    if (error) {
      clearError()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await login(formData.email, formData.password)

      // Requirement 15.4: Redirect to preserved destination after login
      const redirectPath = sessionStorage.getItem('redirect_after_login')
      const locationState = location.state as { from?: { pathname: string } }

      if (onSuccess) {
        onSuccess()
      } else if (redirectPath) {
        // Clear the stored redirect path
        sessionStorage.removeItem('redirect_after_login')
        navigate(redirectPath, { replace: true })
      } else if (locationState?.from) {
        // Redirect to the page they tried to access
        navigate(locationState.from.pathname, { replace: true })
      } else {
        // Default redirect to workspaces
        navigate('/workspaces', { replace: true })
      }
    } catch (err) {
      // Error is handled by the store
    }
  }

  const handleForgotPassword = () => {
    if (onForgotPassword) {
      onForgotPassword()
    } else {
      navigate('/forgot-password')
    }
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <h2 className="auth-form__title">{t('auth.login')}</h2>

      {sessionExpiredMessage && (
        <div className="auth-form__info" role="alert">
          {sessionExpiredMessage}
        </div>
      )}

      {error && (
        <div className="auth-form__error" role="alert">
          {error}
        </div>
      )}

      <Input
        label={t('auth.email')}
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        placeholder={t('auth.enterEmail')}
        autoComplete="email"
        required
      />

      <Input
        label={t('auth.password')}
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        error={errors.password}
        placeholder={t('auth.enterPassword')}
        autoComplete="current-password"
        required
      />

      <div className="auth-form__forgot-password">
        <button
          type="button"
          className="auth-form__link-button"
          onClick={handleForgotPassword}
          disabled={isLoading}
        >
          {t('auth.forgotPassword')}
        </button>
      </div>

      <Button
        type="submit"
        variant="primary"
        loading={isLoading}
        disabled={isLoading}
        fullWidth
      >
        {isLoading ? t('auth.loggingIn') : t('auth.login')}
      </Button>

      <p className="auth-form__footer">
        {t('auth.dontHaveAccount')}{' '}
        <a href="/register" className="auth-form__link">
          {t('auth.register')}
        </a>
      </p>
    </form>
  )
}
